import { useMemo } from 'react';

import {
    EPublisherSolutionModel,
    Portal,
    Project
} from 'common/contracts';

import { useUserDetails } from '../../../api/useUserDetails';
import { generatePortalNavItems, generateProjectNavItems } from '../../../design-system/AcSidebar/utils';
import { ProjectCategory } from '../constants';
import { ProjectItem } from '../types';
import { LastViewedUtils } from '../utils/lastViewedUtils';

export const useProjectsData = () => {
    const { userDetails } = useUserDetails();

    const projectItems: ProjectItem[] = useMemo(() => {
      const projects = userDetails?.projects || [];
      const portals = userDetails?.portals || [];
      const sidebarProjectItems = generateProjectNavItems(projects);
      const sidebarPortalItems = generatePortalNavItems(portals);
      const sidebarItems = [...sidebarPortalItems, ...sidebarProjectItems];

      // Transform sidebar items to ProjectItem format, preserving original data
      const projectCards: ProjectItem[] = sidebarItems.map(
        (sidebarItem: any) => {
          // Determine if it's a project or portal based on URL
          const isPortal = sidebarItem.url.startsWith('portal/');
          const isProject = sidebarItem.url.startsWith('project/');

          // Find original project/portal data to preserve types
          let originalData: any = null;
          if (isProject) {
            originalData = projects.find(
              (p: Project) => p.publisherId === sidebarItem.id
            );
          } else if (isPortal) {
            originalData = portals.find((p: Portal) => p.portalId === sidebarItem.id);
          }

          return {
            id: sidebarItem.id,
            name: sidebarItem.title,
            type: originalData?.projectType || EPublisherSolutionModel.STORE,
            subType: originalData?.projectSubType,
            logo: originalData?.publisherLogo || '',
            thumbnailImageUrl: originalData?.thumbnailImageUrl,
            category: isPortal
              ? ProjectCategory.PORTAL
              : ProjectCategory.PROJECT,
            url: `/${sidebarItem.url}`,
            role: isPortal
              ? 'Portal User'
              : originalData?.role || 'Project User',
            isParkingDomain: originalData?.isParkingDomain,
            tags: originalData?.tags || []
          };
        }
      );

      const allItems = projectCards;

      // Merge with last viewed data from localStorage
      return allItems.map((item) => {
        const lastViewedAt = LastViewedUtils.getProjectLastViewedAt(item.id);
        return {
          ...item,
          lastViewedAt: lastViewedAt || undefined
        };
      });
    }, [userDetails]);

    return {
        projectItems,
        userDetails
    };
};
